import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  Euro,
  Package,
  Download,
  Upload
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

const BillingPositions = ({ isOpen, onClose }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    position_number: '',
    description: '',
    unit: '',
    unit_price: 0
  });

  useEffect(() => {
    if (isOpen) {
      loadPositions();
    }
  }, [isOpen]);

  const loadPositions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/billing-positions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPositions(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Positionen:', error);
      alert('Fehler beim Laden der Positionen');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('authToken');
      
      if (editingId) {
        // Update
        await axios.put(
          `${BACKEND_URL}/api/billing-positions/${editingId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Position erfolgreich aktualisiert!');
      } else {
        // Create
        await axios.post(
          `${BACKEND_URL}/api/billing-positions`,
          { ...formData, is_active: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Position erfolgreich erstellt!');
      }
      
      resetForm();
      loadPositions();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (position) => {
    setFormData({
      position_number: position.position_number,
      description: position.description,
      unit: position.unit,
      unit_price: position.unit_price
    });
    setEditingId(position.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diese Position wirklich deaktivieren?')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${BACKEND_URL}/api/billing-positions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Position erfolgreich deaktiviert!');
      loadPositions();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen der Position');
    }
  };

  const resetForm = () => {
    setFormData({
      position_number: '',
      description: '',
      unit: '',
      unit_price: 0
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/billing-positions/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `abrechnungspositionen_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('Export erfolgreich!');
    } catch (error) {
      console.error('Fehler beim Export:', error);
      alert('Fehler beim Export');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Bitte wählen Sie eine Datei aus');
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/billing-positions/import/excel`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      alert(`Import erfolgreich!\nNeu importiert: ${response.data.imported}\nAktualisiert: ${response.data.updated}${response.data.errors.length > 0 ? '\n\nFehler:\n' + response.data.errors.join('\n') : ''}`);
      setShowImportDialog(false);
      setImportFile(null);
      loadPositions();
    } catch (error) {
      console.error('Fehler beim Import:', error);
      alert(error.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Package className="w-6 h-6 text-emerald-600" />
                Abrechnungspositionen verwalten
              </h2>
              <p className="text-slate-600 mt-1">
                Positionen für die Abrechnung von Aufträgen
              </p>
            </div>
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          {/* Add/Edit Form */}
          {showAddForm ? (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{editingId ? 'Position bearbeiten' : 'Neue Position hinzufügen'}</span>
                  <Button variant="ghost" size="sm" onClick={resetForm}>
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Positionsnummer *
                      </label>
                      <Input
                        value={formData.position_number}
                        onChange={(e) => setFormData({ ...formData, position_number: e.target.value })}
                        placeholder="z.B. 10060202"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Einheit *
                      </label>
                      <Input
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        placeholder="z.B. Stück, Meter, Stunde"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Beschreibung *
                    </label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="z.B. Gf-TA Connect Only"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Einzelpreis (€) *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.unit_price}
                      onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Abbrechen
                    </Button>
                    <Button type="submit">
                      <Save className="w-4 h-4 mr-2" />
                      {editingId ? 'Aktualisieren' : 'Hinzufügen'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : (
            <div className="mb-6 flex gap-3">
              <Button onClick={() => setShowAddForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Neue Position hinzufügen
              </Button>
              <Button variant="outline" onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Excel exportieren
              </Button>
              <Button variant="outline" onClick={() => setShowImportDialog(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Excel importieren
              </Button>
            </div>
          )}

          {/* Positions List */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Lade Positionen...</p>
            </div>
          ) : positions.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Noch keine Positionen vorhanden</p>
              <p className="text-slate-400 text-sm mt-1">
                Klicken Sie auf "Neue Position hinzufügen" um zu beginnen
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {positions.map((position) => (
                <Card key={position.id}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge variant="outline" className="font-mono">
                            {position.position_number}
                          </Badge>
                          <h3 className="font-semibold text-lg">
                            {position.description}
                          </h3>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Euro className="w-4 h-4" />
                            {position.unit_price.toFixed(2)} € / {position.unit}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(position)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(position.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Import Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Excel importieren</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportFile(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-slate-600 mb-3">
                  Laden Sie eine Excel-Datei mit Abrechnungspositionen hoch. Die Datei sollte folgende Spalten enthalten:
                </p>
                <ul className="text-sm text-slate-600 list-disc list-inside mb-4">
                  <li>Positionsnummer</li>
                  <li>Beschreibung</li>
                  <li>Einheit</li>
                  <li>Einzelpreis (€)</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Excel-Datei auswählen
                </label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                />
              </div>

              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportFile(null);
                  }}
                >
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleImport}
                  disabled={!importFile || importing}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importing ? 'Importiere...' : 'Importieren'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BillingPositions;
